function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function candidateBossNo(candidate = {}) {
  return numberValue(candidate.bossNo ?? candidate.boss_no);
}

function candidateDamage(candidate = {}) {
  return numberValue(candidate.damage ?? candidate.damageEok);
}

function candidateMemberKey(candidate = {}) {
  return String(candidate.memberKey ?? candidate.memberOpenid ?? candidate.member_openid ?? '').trim();
}

function candidateDeckKeys(candidate = {}) {
  const source = candidate.deckKeys ?? candidate.deck_keys;
  return Array.isArray(source) ? source : [];
}

function cloneUsageMap(source) {
  const out = new Map();
  if (source instanceof Map) {
    for (const [key, value] of source.entries()) out.set(key, value);
  } else {
    for (const [key, value] of Object.entries(source || {})) out.set(key, value);
  }
  return out;
}

function cloneDeckKeysMap(source) {
  const out = new Map();
  if (source instanceof Map) {
    for (const [key, values] of source.entries()) out.set(key, new Set(values));
  } else {
    for (const [key, values] of Object.entries(source || {})) out.set(key, new Set(values));
  }
  return out;
}

// Candidates for the same boss as `targetId` that aren't already used anywhere in the baseline
// plan — the pool a leader could realistically swap in instead of the current assignment.
export function listSwapCandidates({ input, plan, targetId }) {
  const target = (input?.targets || []).find((item) => item.id === targetId);
  if (!target) return [];
  const usedIds = new Set((plan?.assignments || []).flatMap((assignment) => (assignment.items || []).map((item) => item.id)));
  return (input?.candidates || [])
    .filter((candidate) => candidateBossNo(candidate) === numberValue(target.bossNo) && !usedIds.has(candidate.id))
    .sort((a, b) => candidateDamage(b) - candidateDamage(a));
}

// Forces `alternateCandidateId` onto `targetId` in place of the one hit `replaceItemId`, then lets
// the simulator RE-PLAN that boss around it. Only the alternate is pinned: the replaced slot is
// removed from the pool, but the boss's other current attackers go back into the pool rather than
// being frozen — a stronger alternate may make some of them unnecessary (less overkill, attacks
// freed for other bosses), and a weaker one may need a different combination to still clear.
// Freezing them would just stack the new damage on top of the old combo and keep whatever
// overkill or shortfall that produced.
//
// The alternate is seeded into initialUsage/initialDeckKeys/initialUsedSlotIds — the same mechanism
// real prior attacks use — and the target's remaining HP is reduced by its damage up front. A
// pre-seeded attack is invisible to simulateFullRaid's totalDamage/usedHits counters, so `applied`
// carries its damage/overkill/hit back for an apples-to-apples comparison, and `pin` lets
// summarizeTradeoff rebuild the re-planned boss (alternate + whatever the simulator added).
export function buildSwapScenarioInput({ input, targetId, replaceItemId, alternateCandidateId }) {
  const target = (input?.targets || []).find((item) => item.id === targetId);
  const alternate = (input?.candidates || []).find((item) => item.id === alternateCandidateId);
  if (!input || !target || !alternate) return null;

  const forcedCandidates = (input.candidates || []).filter((item) => item.id !== replaceItemId);

  const originalRemaining = numberValue(target.remaining);
  const damage = candidateDamage(alternate);
  const forcedTargets = (input.targets || []).map((item) => (
    item.id === targetId ? { ...item, remaining: Math.max(0, originalRemaining - damage) } : item
  ));

  const memberKey = candidateMemberKey(alternate);
  const forcedInitialUsage = cloneUsageMap(input.initialUsage);
  forcedInitialUsage.set(memberKey, Math.min(3, (forcedInitialUsage.get(memberKey) || 0) + 1));

  const forcedInitialDeckKeys = cloneDeckKeysMap(input.initialDeckKeys);
  const deckSet = forcedInitialDeckKeys.get(memberKey) || new Set();
  candidateDeckKeys(alternate).forEach((key) => deckSet.add(key));
  forcedInitialDeckKeys.set(memberKey, deckSet);

  const forcedInitialUsedSlotIds = [
    ...(Array.isArray(input.initialUsedSlotIds) ? input.initialUsedSlotIds : []),
    alternate.id,
  ];

  return {
    input: {
      ...input,
      targets: forcedTargets,
      candidates: forcedCandidates,
      initialUsage: forcedInitialUsage,
      initialDeckKeys: forcedInitialDeckKeys,
      initialUsedSlotIds: forcedInitialUsedSlotIds,
    },
    applied: {
      damage,
      overkill: Math.max(0, damage - originalRemaining),
      hits: 1,
    },
    pin: { targetId, alternate, originalRemaining },
  };
}

function assignmentItemKey(assignment) {
  return (assignment?.items || []).map((item) => item.id).sort().join(',');
}

function summarizeTargetAssignment(assignment, originalRemaining) {
  if (!assignment) return { items: [], total: 0, overkill: 0, remaining: originalRemaining, cleared: false };
  return {
    items: assignment.items || [],
    total: numberValue(assignment.total),
    overkill: numberValue(assignment.overkill),
    remaining: numberValue(assignment.remaining),
    cleared: Boolean(assignment.cleared),
  };
}

// Compares a swap scenario's best plan against the baseline plan. `applied` (from
// buildSwapScenarioInput) adds the pinned alternate's own damage/overkill/hit back in, since
// simulateFullRaid never counted it (it treats a pinned candidate like a real prior attack).
//
// Swapping one attacker frees that member's attack for the rest of the raid, so the simulator
// re-plans every other boss too — `changedTargets` names the other bosses whose assignment moved,
// which is usually the real reason the totals shifted by more than the one swapped hit.
// `swappedTarget` shows the swapped boss itself before and after it was re-planned around the
// alternate (the pinned alternate plus whatever the simulator added or dropped to clear it).
export function summarizeTradeoff({ baselinePlan, scenarioResult, applied = { damage: 0, overkill: 0, hits: 0 }, pin = null, swappedTargetId = pin?.targetId || '' }) {
  const scenario = scenarioResult?.best || null;
  if (!baselinePlan || !scenario) {
    return { verdict: 'unknown', decidedBy: 'none', clearedDelta: 0, totalDamageDelta: 0, overkillDelta: 0, usedHitsDelta: 0, baseline: null, scenario: null, changedTargets: [], swappedTarget: null };
  }

  const baselineTotals = {
    cleared: baselinePlan.clearedCount,
    totalDamage: baselinePlan.totalDamage,
    totalOverkill: baselinePlan.totalOverkill,
    usedHits: baselinePlan.usedHits,
  };
  const scenarioTotals = {
    cleared: scenario.clearedCount,
    totalDamage: scenario.totalDamage + numberValue(applied.damage),
    totalOverkill: scenario.totalOverkill + numberValue(applied.overkill),
    usedHits: scenario.usedHits + numberValue(applied.hits),
  };

  const clearedDelta = scenarioTotals.cleared - baselineTotals.cleared;
  const totalDamageDelta = scenarioTotals.totalDamage - baselineTotals.totalDamage;
  const overkillDelta = scenarioTotals.totalOverkill - baselineTotals.totalOverkill;
  const usedHitsDelta = scenarioTotals.usedHits - baselineTotals.usedHits;

  // Same priority the simulator itself ranks states by (raidFullSimulator.js stateRank): clearing
  // more bosses wins first, then wasting less overkill, then spending fewer attacks. Raw total
  // damage is only the last tiebreak — damage past a boss's remaining HP is wasted, so a bigger
  // total is not automatically better.
  let verdict = 'even';
  let decidedBy = 'none';
  if (clearedDelta !== 0) {
    verdict = clearedDelta > 0 ? 'gain' : 'loss';
    decidedBy = 'cleared';
  } else if (Math.abs(overkillDelta) > 1e-6) {
    verdict = overkillDelta < 0 ? 'gain' : 'loss';
    decidedBy = 'overkill';
  } else if (usedHitsDelta !== 0) {
    verdict = usedHitsDelta < 0 ? 'gain' : 'loss';
    decidedBy = 'hits';
  } else if (Math.abs(totalDamageDelta) > 1e-6) {
    verdict = totalDamageDelta > 0 ? 'gain' : 'loss';
    decidedBy = 'damage';
  }

  const baselineByTarget = new Map((baselinePlan.assignments || []).map((assignment) => [assignment.target.id, assignment]));
  const scenarioByTarget = new Map((scenario.assignments || []).map((assignment) => [assignment.target.id, assignment]));
  const changedTargets = [];
  for (const targetId of new Set([...baselineByTarget.keys(), ...scenarioByTarget.keys()])) {
    if (targetId === swappedTargetId) continue;
    if (assignmentItemKey(baselineByTarget.get(targetId)) === assignmentItemKey(scenarioByTarget.get(targetId))) continue;
    const target = (scenarioByTarget.get(targetId) || baselineByTarget.get(targetId)).target;
    changedTargets.push({ id: targetId, level: target.level, bossNo: target.bossNo, bossName: target.bossName });
  }
  changedTargets.sort((a, b) => a.level - b.level || a.bossNo - b.bossNo);

  let swappedTarget = null;
  if (pin?.targetId && pin.alternate) {
    const originalRemaining = numberValue(pin.originalRemaining);
    // Whatever the simulator committed to the swapped boss on top of the pinned alternate. When the
    // alternate alone covers the boss this is a zero-cost "already cleared" entry with no items.
    const reassigned = scenarioByTarget.get(pin.targetId);
    const total = candidateDamage(pin.alternate) + numberValue(reassigned?.total);
    const cleared = total + 1e-9 >= originalRemaining;
    swappedTarget = {
      originalRemaining,
      before: summarizeTargetAssignment(baselineByTarget.get(pin.targetId), originalRemaining),
      after: {
        items: [pin.alternate, ...(reassigned?.items || [])],
        total,
        overkill: cleared ? Math.max(0, total - originalRemaining) : 0,
        remaining: cleared ? 0 : Math.max(0, originalRemaining - total),
        cleared,
      },
    };
  }

  return {
    verdict,
    decidedBy,
    clearedDelta,
    totalDamageDelta,
    overkillDelta,
    usedHitsDelta,
    baseline: baselineTotals,
    scenario: scenarioTotals,
    changedTargets,
    swappedTarget,
  };
}
